import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getPayrolls } from "services/payrolls";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const fmtMoney = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

const TYPE_LABELS = { regular: 'Regular', off_cycle: 'Off-Cycle', correction: 'Correction', '1099_only': '1099 Only' };
const TYPE_COLORS = { regular: '#0d6efd', off_cycle: '#6f42c1', correction: '#1B75F0', '1099_only': '#198754' };

const AllReports = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const { data: payrollsRaw, isLoading } = useQuery({
        queryKey: ['payrolls'],
        queryFn: getPayrolls,
        staleTime: 5 * 60 * 1000,
    });

    const payrolls = useMemo(() => {
        const list = Array.isArray(payrollsRaw) ? payrollsRaw : (payrollsRaw?.data ?? []);
        return [...list]
            .filter(p => p.status === 'approved')
            .sort((a, b) => new Date(b.payDate || b.createdAt) - new Date(a.payDate || a.createdAt));
    }, [payrollsRaw]);

    const filtered = useMemo(() => {
        if (!search.trim()) return payrolls;
        const q = search.toLowerCase();
        return payrolls.filter(p => {
            const type = (TYPE_LABELS[p.type] || p.type || '').toLowerCase();
            const period = `${fmt(p.startDate)} ${fmt(p.endDate)}`.toLowerCase();
            const payDate = fmt(p.payDate).toLowerCase();
            return type.includes(q) || period.includes(q) || payDate.includes(q);
        });
    }, [payrolls, search]);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                    {payrolls.length} approved payroll{payrolls.length !== 1 ? 's' : ''}
                </p>
                <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by type, period, or pay date..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ maxWidth: 280, fontSize: 13 }}
                />
            </div>

            {isLoading ? (
                <p className="text-center text-muted py-4">Loading reports...</p>
            ) : filtered.length === 0 ? (
                <p className="text-center text-muted py-4">
                    {payrolls.length === 0 ? 'No approved payrolls found.' : 'No results match your search.'}
                </p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover" style={{ fontSize: 13 }}>
                        <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                                <th>Name</th>
                                <th>Pay Period</th>
                                <th>Pay Date</th>
                                <th>Type</th>
                                <th className="text-end">Gross Pay</th>
                                <th className="text-end">Total Taxes</th>
                                <th className="text-end">Net Pay</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <span
                                            style={{ color: '#1B75F0', cursor: 'pointer', fontWeight: 500 }}
                                            onClick={() => navigate(`/dashboard/payroll/${p.id}`)}
                                        >
                                            Payroll Summary
                                        </span>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                            This report contains the summed employee and contractor earnings and taxes for the report period
                                        </div>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {p.startDate && p.endDate ? `${fmt(p.startDate)} – ${fmt(p.endDate)}` : '—'}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(p.payDate)}</td>
                                    <td>
                                        <span style={{
                                            background: TYPE_COLORS[p.type] + '22',
                                            color: TYPE_COLORS[p.type] || '#333',
                                            padding: '2px 8px',
                                            borderRadius: 4,
                                            fontSize: 12,
                                            fontWeight: 500,
                                        }}>
                                            {TYPE_LABELS[p.type] || p.type || 'Regular'}
                                        </span>
                                    </td>
                                    <td className="text-end">{fmtMoney(p.grossPay)}</td>
                                    <td className="text-end">{fmtMoney(p.totalTaxes)}</td>
                                    <td className="text-end">{fmtMoney(p.netPay)}</td>
                                    <td>
                                        <button
                                            className="btn btn-sm"
                                            style={{ fontSize: 12, background: '#1B75F0', color: '#fff', border: 'none' }}
                                            onClick={() => navigate(`/dashboard/payroll/${p.id}`)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AllReports;
